import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';

const Section = styled.div`
  background-color: var(--clr-bg);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #4a90d9;
  color: var(--clr-sec);
  font-size: 0.85rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.75rem;
  color: white;
  background-color: ${(props) => props.color};
`;

const WhyText = styled.p`
  margin: 0.5rem 0 0 0;
  opacity: 0.8;
  font-size: 0.78rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const SmallButton = styled.button`
  padding: 3px 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: var(--clr-sec);
  font-size: 0.75rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LEVEL_BADGES = {
  0: { name: 'Restricted', color: '#ff4444' },
  1: { name: 'New', color: '#ff8c00' },
  2: { name: 'Member', color: '#4a90d9' },
  3: { name: 'Regular', color: '#3cb371' },
};

/**
 * Mod-facing trust summary for a user: current level, why it's what it is
 * (latest trust event), and manual override controls. Override actions are
 * global-mod only — the server enforces that; non-global mods just see the
 * error from the API.
 */
function ModTrustSection({ userId }) {
  const [trust, setTrust] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const loadTrust = useCallback(async () => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/trust/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setTrust(await response.json());
      }
    } catch (err) {
      // Non-critical mod context — fail silently
      console.error('Failed to load trust info:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      setTrust(null);
      setActionError('');
      loadTrust();
    }
  }, [userId, loadTrust]);

  const setOverride = async (level) => {
    setActionLoading(true);
    setActionError('');
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/trust-override`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, level }),
      });
      if (response.ok) {
        await loadTrust();
      } else {
        const errorData = await response.json();
        setActionError(errorData.error || 'Failed to update trust');
      }
    } catch {
      setActionError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  if (!trust) return null;

  const badge = LEVEL_BADGES[trust.trustLevel] || LEVEL_BADGES[1];
  const latestEvent = trust.events && trust.events[0];

  return (
    <Section>
      <Row>
        <span>
          Trust: <Badge color={badge.color}>{badge.name}</Badge>
          {trust.manualOverride !== null && ' (mod override)'}
          {trust.isShadowTreated && ' '}
          {trust.isShadowTreated && <Badge color="#777">Shadowed</Badge>}
        </span>
        <span style={{ opacity: 0.7 }}>
          {trust.cleanMessageCount} msgs · {trust.accountAgeHours}h old
        </span>
      </Row>
      {latestEvent && latestEvent.reason && (
        <WhyText>Latest: {latestEvent.reason}</WhyText>
      )}
      {actionError && <WhyText style={{ color: '#ff6666' }}>{actionError}</WhyText>}
      <Actions>
        <SmallButton
          type="button"
          disabled={actionLoading || trust.trustLevel === 0}
          onClick={() => setOverride(0)}
        >
          Restrict
        </SmallButton>
        <SmallButton
          type="button"
          disabled={actionLoading || (trust.manualOverride === null ? trust.trustLevel >= 2 : trust.manualOverride === 2)}
          onClick={() => setOverride(2)}
        >
          Trust (Member)
        </SmallButton>
        <SmallButton
          type="button"
          disabled={actionLoading || trust.manualOverride === null}
          onClick={() => setOverride(null)}
        >
          Clear override
        </SmallButton>
      </Actions>
    </Section>
  );
}

export default ModTrustSection;
